import { useState, useEffect, useCallback } from 'react';

import { supabase } from '../lib/supabase';
import { useAuth } from '../providers/AuthProvider';

/**
 * Kim jest partner w tej sesji — jedno źródło prawdy dla całego panelu.
 * ====================================================================
 *
 * Rozstrzyga to BAZA (`partner_kontekst()`, migracja 463), nie przeglądarka.
 * Powód jest taki, że przeglądarka nie zna trzech rzeczy naraz:
 *
 *   1. Rekord partnera bywa zakładany przez super-admina, ZANIM partner
 *      założy konto — `partners.user_id` jest wtedy pusty. Poprzednia wersja
 *      tego hooka pytała wyłącznie `WHERE user_id = auth.uid()`, więc partner
 *      logujący się na własne konto dostawał „Nie znaleziono profilu partnera".
 *      Funkcja dopina konto po adresie e-mail z tokenu — czego z frontu zrobić
 *      się nie da, bo polityka UPDATE na `partners` wymaga dokładnie tego
 *      `user_id`, którego jeszcze nie ma.
 *   2. W trybie podglądu („Przeglądasz panel jako partner") zalogowany jest
 *      super-admin. Każde pytanie po `auth.uid()` zwracało pustkę, więc podgląd
 *      pokazywał ramkę bez danych. Teraz wskazanego partnera podajemy wprost,
 *      a baza sprawdza, czy wołający ma prawo do podglądu.
 *   3. Wejście partnera w panel wymaga członkostwa `role = 'partner'` w jego
 *      własnej organizacji — funkcja domyka je przy pierwszym wywołaniu.
 *
 * `partnerId` (rekord w `partners`) i `partnerOrgId` (organizacja partnera) to
 * DWA RÓŻNE identyfikatory i nie wolno ich mylić:
 *
 *   • `organizations.partner_id`     → wskazuje ORGANIZACJĘ partnera  (partnerOrgId)
 *   • `partner_commissions.partner_id` → wskazuje REKORD partnera     (partnerId)
 *   • `partner_regions.partner_id`   → wskazuje REKORD partnera       (partnerId)
 */
export interface PartnerRow {
  id: string;
  user_id: string | null;
  organization_id: string | null;
  company_name: string | null;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  voivodeship: string | null;
  tax_id: string | null;
  regon: string | null;
  krs: string | null;
  bank_account: string | null;
  commission_rate: number | null;
  regions: string[] | null;
  settings: Record<string, unknown> | null;
  status: string | null;
  [key: string]: unknown;
}

export interface PartnerContext {
  /** ID rekordu w tabeli `partners` — klucz prowizji i zasięgu. */
  partnerId: string | null;
  /** `partners.organization_id` — klucz listy klientów. null = partner nieaktywowany. */
  partnerOrgId: string | null;
  partner: PartnerRow | null;
  /** true, gdy panel jest oglądany przez super-admina w trybie podglądu. */
  podglad: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface WierszKontekstu {
  partner_id: string;
  organization_id: string | null;
  company_name: string | null;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  voivodeship: string | null;
  tax_id: string | null;
  regon: string | null;
  krs: string | null;
  bank_account: string | null;
  status: string | null;
  commission_rate: number | null;
  regions: string[] | null;
  settings: Record<string, unknown> | null;
  podglad: boolean;
  powiazano: boolean;
}

export function usePartnerContext(): PartnerContext {
  const { user, impersonatingPartner } = useAuth();
  const [partner, setPartner] = useState<PartnerRow | null>(null);
  const [podglad, setPodglad] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const podgladanyId = impersonatingPartner?.id ?? null;

  const fetchPartner = useCallback(async () => {
    if (!user) {
      setPartner(null);
      setPodglad(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: rpcError } = await supabase.rpc('partner_kontekst', {
        p_partner_id: podgladanyId,
      });

      if (rpcError) throw rpcError;

      const wiersz = ((data as WierszKontekstu[]) ?? [])[0] ?? null;

      if (!wiersz) {
        setPartner(null);
        setPodglad(false);
        setError(
          user.is_super_admin
            ? 'Nie znaleziono profilu partnera. Wejdź w podgląd z listy partnerów w panelu super-admina.'
            : 'Twoje konto nie jest powiązane z żadnym profilem partnera. Skontaktuj się z administratorem.'
        );
        return;
      }

      setPodglad(Boolean(wiersz.podglad));
      setPartner({
        id: wiersz.partner_id,
        user_id: user.id,
        organization_id: wiersz.organization_id,
        company_name: wiersz.company_name,
        contact_name: wiersz.contact_name,
        email: wiersz.email,
        phone: wiersz.phone,
        address: wiersz.address,
        city: wiersz.city,
        postal_code: wiersz.postal_code,
        voivodeship: wiersz.voivodeship,
        tax_id: wiersz.tax_id,
        regon: wiersz.regon,
        krs: wiersz.krs,
        bank_account: wiersz.bank_account,
        commission_rate: wiersz.commission_rate,
        regions: wiersz.regions,
        settings: wiersz.settings ?? {},
        status: wiersz.status,
      });

      if (!wiersz.organization_id) {
        setError(
          'Konto partnera nie zostało jeszcze aktywowane — brak przypisanej organizacji. Skontaktuj się z administratorem.'
        );
      }
    } catch (err) {
      console.error('Error fetching partner context:', err);
      setPartner(null);
      setPodglad(false);
      setError(err instanceof Error ? err.message : 'Nie udało się załadować profilu partnera');
    } finally {
      setLoading(false);
    }
  }, [user, podgladanyId]);

  useEffect(() => {
    fetchPartner();
  }, [fetchPartner]);

  return {
    partnerId: partner?.id ?? null,
    partnerOrgId: partner?.organization_id ?? null,
    partner,
    podglad,
    loading,
    error,
    refetch: fetchPartner,
  };
}

export default usePartnerContext;
